import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { Shield, ArrowRight, Lock } from 'lucide-react';

const ADMIN_EMAIL = 'hello@subguardhq.com';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [debugOtp, setDebugOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (email !== ADMIN_EMAIL) {
      toast.error('Unauthorized email');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await adminApi.sendOtp(email);
      if (response.data.debug_otp) {
        setDebugOtp(response.data.debug_otp);
      }
      toast.success('OTP sent');
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Enter 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const response = await adminApi.verifyOtp(email, otp);
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="w-10 h-10 text-[#6ee7b7]" />
          <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            SubGuard Admin
          </span>
        </div>

        <Card className="border-0 bg-gray-800">
          <CardHeader>
            <CardTitle className="text-white text-center">
              {step === 'email' ? 'Admin Login' : 'Enter OTP'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 'email' ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Admin email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#6ee7b7] text-gray-900 hover:bg-[#5dd3a3]"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                {debugOtp && (
                  <div className="p-4 bg-yellow-900/50 border border-yellow-600 rounded-xl">
                    <p className="text-sm text-yellow-200 font-medium">Debug OTP:</p>
                    <p className="text-2xl font-bold text-yellow-100 tracking-widest mt-1">{debugOtp}</p>
                  </div>
                )}
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot 
                          key={index} 
                          index={index} 
                          className="w-12 h-14 text-xl font-semibold bg-gray-700 border-gray-600 text-white"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#6ee7b7] text-gray-900 hover:bg-[#5dd3a3]"
                  disabled={isLoading || otp.length !== 6}
                >
                  {isLoading ? 'Verifying...' : 'Verify & Login'}
                  <Lock className="w-4 h-4 ml-2" />
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(''); setDebugOtp(''); }}
                  className="w-full text-sm text-gray-400 hover:text-white"
                >
                  Back
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
